import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Lock, 
  Unlock, 
  AlertCircle,
  User as UserIcon,
  KeyRound,
  FileText,
} from 'lucide-react';
import { adminReceiptApi, AdminReceiptInfo, FreePlanInfo, planApi } from '@/api/planApi';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { aiUsageApi, AiUsageTopUser } from '@/api/dashboardApi';

const API_BASE_URL = `${BASE}/api`;

interface User {
  userId: string;
  name: string;
  email: string;
  phone?: string;
  companyName?: string;
  businessNumber?: string;
  userRole: 'SUPER_ADMIN' | 'CLIENT';
  approvalStatus: 'APPROVED' | 'PENDING' | 'REJECTED';
  approvalReason?: string;
  subscriptionPlan: 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  isLocked: boolean;
  failedLoginCount: number;
  lastLoginAt?: string;
  createdAt: string;
  loginType: 'LOCAL' | 'GOOGLE' | 'NAVER' | 'KAKAO';
  enabled?: boolean;
  isWithdrawn?: boolean;
  withdrawnAt?: string;
  // 팀 정보 필드
  teamName?: string;
  teamRole?: 'OWNER' | 'MEMBER';
  teamId?: number;
  teamPlan?: 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  teamUsageLimit?: number;
  teamUsageCount?: number;
  teamBillingCycleDay?: number;
  // 개인 결제일
  billingCycleDay?: number;
}

interface UsersListResponse {
  success: boolean;
  message: string;
  data: User[];
  totalPages: number;
  totalElements: number;
  currentPage: number;
  pageSize: number;
}

interface UserDetailResponse {
  success?: boolean;
  message?: string;
  data?: User;
}

interface UserUpdateRequest {
  name?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  businessNumber?: string;
  userRole?: 'SUPER_ADMIN' | 'CLIENT';
  approvalStatus?: 'APPROVED' | 'PENDING' | 'REJECTED';
  approvalReason?: string;
  subscriptionPlan?: 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  enabled?: boolean;
}

// ── 개인정보 마스킹 유틸 (개인정보 보호법 / KISA 가이드라인) ──────────────

/** 이름 마스킹
 *  1자: *  /  2자: 홍*  /  3자: 홍*동  /  4자+: 홍**동
 */
const maskName = (name: string): string => {
  if (!name) return '';
  if (name.length === 1) return '*';
  if (name.length === 2) return name[0] + '*';
  // 3자 이상: 첫 글자 + 중간 모두 * + 마지막 글자
  return name[0] + '*'.repeat(name.length - 2) + name[name.length - 1];
};

/** 이메일 마스킹
 *  ID 앞 2자 노출 후 *** 처리 (도메인 전체 노출)
 *  예) test@example.com → te***@example.com
 */
const maskEmail = (email: string): string => {
  if (!email) return '';
  const atIdx = email.indexOf('@');
  if (atIdx < 0) return email;
  const id = email.substring(0, atIdx);
  const domain = email.substring(atIdx);
  const visibleLen = Math.min(2, id.length);
  return id.substring(0, visibleLen) + '***' + domain;
};


const RECEIPT_TYPE_LABEL: Record<string, string> = {
  CASH_RECEIPT: '현금영수증',
  TAX_INVOICE: '세금계산서',
  NONE: '발행안함',
};

const CASH_RECEIPT_PURPOSE_LABEL: Record<string, string> = {
  PERSONAL_DEDUCTION: '개인 소득공제용',
  BUSINESS_EXPENSE: '사업자 지출증빙용',
};

const getNormalizedLoginType = (loginType?: string, userId?: string): User['loginType'] => {
  const normalizedLoginType = loginType?.toUpperCase();

  if (
    normalizedLoginType === 'LOCAL' ||
    normalizedLoginType === 'GOOGLE' ||
    normalizedLoginType === 'NAVER' ||
    normalizedLoginType === 'KAKAO'
  ) {
    return normalizedLoginType as User['loginType'];
  }

  if (userId?.includes('_google_')) {
    return 'GOOGLE';
  }

  if (userId?.includes('_naver_')) {
    return 'NAVER';
  }

  if (userId?.includes('_kakao_')) {
    return 'KAKAO';
  }

  return 'LOCAL';
};

const isSocialLoginUser = (loginType?: string, userId?: string): boolean => {
  return getNormalizedLoginType(loginType, userId) !== 'LOCAL';
};

const getSocialProviderLabel = (loginType?: string, userId?: string): string | null => {
  const normalizedLoginType = getNormalizedLoginType(loginType, userId);

  if (normalizedLoginType === 'LOCAL') {
    return null;
  }

  const labelMap: Record<Exclude<User['loginType'], 'LOCAL'>, string> = {
    GOOGLE: 'Google',
    NAVER: '네이버',
    KAKAO: '카카오',
  };

  return labelMap[normalizedLoginType];
};

const getLoginTypeBadgeClass = (loginType: User['loginType']): string => {
  const classMap: Record<User['loginType'], string> = {
    LOCAL: 'bg-blue-100 text-blue-800',
    GOOGLE: 'bg-green-100 text-green-800',
    NAVER: 'bg-emerald-100 text-emerald-800',
    KAKAO: 'bg-yellow-100 text-yellow-800',
  };

  return classMap[loginType];
};

// ─────────────────────────────────────────────────────────────────────────────

export const UserManagement: React.FC = () => {
  const { isLoading, isAuthenticated } = useAuth();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const [users, setUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED'>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'APPROVED' | 'REJECTED'>('APPROVED');
  const [approvalReason, setApprovalReason] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [detailUser, setDetailUser] = useState<User | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [isDetailApproving, setIsDetailApproving] = useState(false);
  const [editForm, setEditForm] = useState<UserUpdateRequest>({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    businessNumber: '',
    userRole: 'CLIENT',
    approvalStatus: 'PENDING',
    approvalReason: '',
    subscriptionPlan: 'FREE',
    enabled: true,
  });
  const [detailApprovalReason, setDetailApprovalReason] = useState('');
  const [detailReceiptInfo, setDetailReceiptInfo] = useState<AdminReceiptInfo | null>(null);
  const [detailReceiptLoading, setDetailReceiptLoading] = useState(false);
  const [aiTopUsers, setAiTopUsers] = useState<AiUsageTopUser[]>([]);
  const [planLimits, setPlanLimits] = useState<Record<string, number>>({});
  const detailLoginType = detailUser ? getNormalizedLoginType(detailUser.loginType, detailUser.userId) : 'LOCAL';
  const detailIsSocialLoginUser = detailUser ? isSocialLoginUser(detailUser.loginType, detailUser.userId) : false;
  const detailSocialProviderLabel = detailUser ? getSocialProviderLabel(detailUser.loginType, detailUser.userId) : null;

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }
    fetchUsers();
    // 플랜 제한 정보 가져오기
    planApi.getPlans()
      .then((res) => {
        const limits: Record<string, number> = {};
        res.data.forEach((plan) => {
          limits[plan.planId] = plan.monthlyLimit;
        });
        setPlanLimits(limits);
      })
      .catch(() => {
        // API 호출 실패 시 기본값 설정
        setPlanLimits({
          FREE: 60,
          STARTER: 1000,
          PROFESSIONAL: 5000,
          ENTERPRISE: 0,
        });
      });
    // AI 사용량 TOP 조회 (최초 1회)
    aiUsageApi.getTopUsers()
      .then(setAiTopUsers)
      .catch(() => {}); // 권한 없으면 무시
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filter, isLoading, isAuthenticated]);

  const getToken = () => {
    return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  };

  const fetchUsers = async () => {
    try {
      const token = getToken();
      const statusParam = filter !== 'ALL' ? `&approvalStatus=${filter}` : '';
      const searchParam = searchTerm ? `&keyword=${encodeURIComponent(searchTerm)}` : '';
      const response = await axios.get<UsersListResponse>(
        `${API_BASE_URL}/users?page=${currentPage}&size=20${statusParam}${searchParam}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // 디버깅: 사용자 목록에서 loginType 확인
      const normalizedLoginTypes = response.data.data.map((user) => getNormalizedLoginType(user.loginType, user.userId));
      console.log('📋 사용자 목록 로드:', {
        totalUsers: response.data.data.length,
        users: response.data.data.map(u => ({ 
          userId: u.userId, 
          loginType: getNormalizedLoginType(u.loginType, u.userId),
        })),
        localUsers: normalizedLoginTypes.filter((loginType) => loginType === 'LOCAL').length,
        socialUsers: normalizedLoginTypes.filter((loginType) => loginType !== 'LOCAL').length,
      });
      
      setUsers(response.data.data);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || '사용자 목록을 불러오는데 실패했습니다.');
      console.error('Failed to fetch users:', err);
    }
  };

  const openDetail = async (userId: string) => {
    try {
      setDetailLoading(true);
      setDetailError(null);
      const token = getToken();
      const response = await axios.get<UserDetailResponse>(
        `${API_BASE_URL}/users/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const userData = response.data.data ?? (response.data as unknown as User);
      const normalizedLoginType = getNormalizedLoginType(userData.loginType, userData.userId);
      
      // 디버깅: 사용자 정보 확인
      console.log('🔍 회원 상세 정보:', {
        userId: userData.userId,
        name: userData.name,
        loginType: userData.loginType,
        normalizedLoginType,
        email: userData.email,
        isLocalUser: normalizedLoginType === 'LOCAL',
        isSocialUser: normalizedLoginType !== 'LOCAL',
      });
      
      setDetailUser(userData);
      setEditForm({
        name: userData.name,
        email: userData.email,
        phone: userData.phone || '',
        companyName: userData.companyName || '',
        businessNumber: userData.businessNumber || '',
        userRole: userData.userRole,
        approvalStatus: userData.approvalStatus,
        approvalReason: userData.approvalReason || '',
        subscriptionPlan: userData.subscriptionPlan,
        enabled: userData.enabled ?? true,
      });
      setDetailApprovalReason('');
      setDetailReceiptInfo(null);
      setShowDetailModal(true);

      // 증빙 설정 조회 (플랜 무관하게 항상 조회)
      setDetailReceiptLoading(true);
      adminReceiptApi.getUserReceiptInfo(userData.userId)
        .then(res => {
          const body = res.data;
          // B/E가 엔티티 직접 반환 (래핑 없음)
          setDetailReceiptInfo(body && body.receiptType ? body : null);
        })
        .catch(() => setDetailReceiptInfo(null))
        .finally(() => setDetailReceiptLoading(false));
    } catch (err: any) {
      setDetailError(err.response?.data?.message || '회원 상세 정보를 불러오는데 실패했습니다.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!detailUser) return;

    try {
      const token = getToken();
      const socialUser = isSocialLoginUser(detailUser.loginType, detailUser.userId);
      const updatePayload: UserUpdateRequest = {
        ...editForm,
        name: socialUser ? detailUser.name : editForm.name,
        email: socialUser ? detailUser.email : editForm.email,
      };

      await axios.put(
        `${API_BASE_URL}/users/${detailUser.userId}`,
        updatePayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setSuccess('회원 정보가 수정되었습니다.');
      fetchUsers();
      setShowDetailModal(false);
      setDetailUser(null);
    } catch (err: any) {
      setDetailError(err.response?.data?.message || '회원 정보 수정에 실패했습니다.');
    }
  };

  const handleDetailApproval = async (action: 'APPROVED' | 'REJECTED') => {
    if (!detailUser) return;
    if (isDetailApproving) return;

    if (action === 'REJECTED' && !detailApprovalReason.trim()) {
      setDetailError('거부 사유를 입력해주세요.');
      return;
    }

    try {
      setIsDetailApproving(true);
      const token = getToken();
      const endpoint = action === 'APPROVED'
        ? `${API_BASE_URL}/users/${detailUser.userId}/approve`
        : `${API_BASE_URL}/users/${detailUser.userId}/reject`;

      await axios.post(
        endpoint,
        { reason: detailApprovalReason || '승인 완료' },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setSuccess('승인 상태가 변경되었습니다.');
      fetchUsers();
      setShowDetailModal(false);
      setDetailUser(null);
    } catch (err: any) {
      setDetailError(err.response?.data?.message || '승인 상태 변경에 실패했습니다.');
    } finally {
      setIsDetailApproving(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(0);
    fetchUsers();
  };

  const handleApprovalChange = async () => {
    if (!selectedUser) return;
    if (isApproving) return;

    if (approvalAction === 'REJECTED' && !approvalReason.trim()) {
      setError('거부 사유를 입력해주세요.');
      return;
    }

    try {
      setIsApproving(true);
      const token = getToken();
      const endpoint = approvalAction === 'APPROVED' 
        ? `${API_BASE_URL}/users/${selectedUser.userId}/approve`
        : `${API_BASE_URL}/users/${selectedUser.userId}/reject`;
      
      await axios.post(endpoint, 
        { reason: approvalReason || '승인 완료' },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setSuccess(`${selectedUser.name}님의 승인 상태가 변경되었습니다.`);
      setShowApprovalModal(false);
      setSelectedUser(null);
      setApprovalReason('');
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || '승인 상태 변경에 실패했습니다.');
    } finally {
      setIsApproving(false);
    }
  };

  const handleUnlock = async (userId: string, name: string) => {
    if (!window.confirm(`${name}님의 계정 잠금을 해제하시겠습니까?`)) {
      return;
    }

    try {
      const token = getToken();
      await axios.put(
        `${API_BASE_URL}/users/${userId}/unlock`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSuccess(`${name}님의 계정 잠금이 해제되었습니다.`);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || '계정 잠금 해제에 실패했습니다.');
    }
  };

  const handleResetPassword = async (userId: string, name: string, email: string) => {
    const socialProviderLabel = getSocialProviderLabel(detailUser?.loginType, userId);

    if (socialProviderLabel) {
      setDetailError(`${socialProviderLabel} 간편로그인 사용자는 비밀번호를 초기화할 수 없습니다.`);
      return;
    }

    if (!email) {
      setDetailError('이메일 정보가 없어 비밀번호를 초기화할 수 없습니다.');
      return;
    }

    if (!window.confirm(
      `${name}님의 비밀번호를 초기화하시겠습니까?\n\n` +
      `새로운 임시 비밀번호가 ${email}로 전송됩니다.`
    )) {
      return;
    }

    try {
      const token = getToken();
      await axios.post(
        `${API_BASE_URL}/users/${userId}/reset-password`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSuccess(`${name}님의 비밀번호가 초기화되었습니다. 임시 비밀번호가 이메일로 전송되었습니다.`);
      if (showDetailModal) {
        setShowDetailModal(false);
        setDetailUser(null);
      }
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || '비밀번호 초기화에 실패했습니다.');
    }
  };

  const getStatusBadge = (status: string) => {
    const config = {
      PENDING: { className: 'bg-yellow-200 text-yellow-800', icon: Clock, label: '대기' },
      APPROVED: { className: 'bg-green-200 text-green-800', icon: CheckCircle2, label: '승인' },
      REJECTED: { className: 'bg-red-200 text-red-800', icon: XCircle, label: '거부' },
    };

    const { className, icon: Icon, label } = config[status as keyof typeof config];

    return (
      <Badge className={`flex items-center gap-1 w-fit ${className}`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getRoleBadge = (role: string) => {
    return role === 'SUPER_ADMIN' ? (
      <Badge className="bg-purple-200 text-purple-800">관리자</Badge>
    ) : (
      <Badge className="bg-blue-200 text-blue-800">일반</Badge>
    );
  };

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || '회원 관리'}
        description="시스템 사용자 관리 및 승인"
        icon={menuIcon}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-4 text-sm hover:underline">
              닫기
            </button>
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 flex items-center justify-between">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)} className="ml-4 text-sm hover:underline">
              닫기
            </button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <CardTitle>회원 목록</CardTitle>
              <CardDescription>전체 {totalElements}명</CardDescription>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="이름, 아이디, 이메일 검색"
                className="flex-1 md:w-64"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch}>
                <Search className="w-4 h-4 mr-2" />
                검색
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs
            value={filter}
            onValueChange={(v) => {
              setFilter(v as typeof filter);
              setCurrentPage(0);
            }}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="ALL">전체</TabsTrigger>
              <TabsTrigger value="PENDING">대기</TabsTrigger>
              <TabsTrigger value="APPROVED">승인</TabsTrigger>
              <TabsTrigger value="REJECTED">거부</TabsTrigger>
            </TabsList>

            <TabsContent value={filter} className="mt-6">
              {users.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <UserIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>사용자가 없습니다</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>아이디</TableHead>
                          <TableHead>이름</TableHead>
                          <TableHead>이메일</TableHead>
                          <TableHead>팀(직책)</TableHead>
                          <TableHead>권한</TableHead>
                          <TableHead>상태</TableHead>
                          <TableHead>로그인타입</TableHead>
                          <TableHead>플랜</TableHead>
                          <TableHead className="text-right">이번달 AI</TableHead>
                          <TableHead>결제일</TableHead>
                          <TableHead>가입일</TableHead>
                          <TableHead>작업</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow
                            key={user.userId}
                            className={`cursor-pointer ${user.isWithdrawn ? 'bg-gray-50 opacity-60' : ''}`}
                            onClick={() => openDetail(user.userId)}
                          >
                            <TableCell className="font-medium">
                              {user.userId}
                              {user.isWithdrawn && (
                                <Badge className="ml-2 bg-gray-400 text-white text-xs px-1.5 py-0">탈퇴</Badge>
                              )}
                              {user.isLocked && (
                                <Lock className="w-4 h-4 inline ml-2 text-red-500" />
                              )}
                            </TableCell>
                            <TableCell>{maskName(user.name)}</TableCell>
                            <TableCell>{maskEmail(user.email)}</TableCell>
                            <TableCell>
                              {user.teamName ? (
                                <span>
                                  {user.teamName}
                                  <span className="text-gray-500 ml-1">
                                    ({user.teamRole === 'OWNER' ? '팀장' : '팀원'})
                                  </span>
                                </span>
                              ) : (
                                <span>{user.companyName || '-'}</span>
                              )}
                            </TableCell>
                            <TableCell>{getRoleBadge(user.userRole)}</TableCell>
                            <TableCell>{getStatusBadge(user.approvalStatus)}</TableCell>
                            <TableCell>
                              {(() => {
                                const loginType = getNormalizedLoginType(user.loginType, user.userId);
                                
                                return (
                                  <Badge className={getLoginTypeBadgeClass(loginType)}>
                                    {loginType}
                                  </Badge>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              {user.teamName ? (
                                <div>
                                  <Badge className="border">{user.teamPlan}</Badge>
                                  <div className="text-xs text-gray-500 mt-1">
                                    개인: {user.subscriptionPlan}
                                  </div>
                                </div>
                              ) : (
                                <Badge className="border">{user.subscriptionPlan}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {(() => {
                                const usage = aiTopUsers.find(u => u.userId === user.userId);
                                const personalCnt = usage?.count ?? 0;
                                const personalLimit = planLimits[user.subscriptionPlan] ?? 0;
                                
                                if (user.teamName) {
                                  const teamLimit = user.teamUsageLimit ?? (user.teamPlan ? planLimits[user.teamPlan] : 0) ?? 0;
                                  return (
                                    <div>
                                      <div>{personalCnt.toLocaleString()}건({teamLimit.toLocaleString()}건)</div>
                                      <div className="text-xs text-gray-500">
                                        개인: {personalLimit.toLocaleString()}건
                                      </div>
                                    </div>
                                  );
                                }
                                
                                return (
                                  <span>
                                    {personalCnt.toLocaleString()}건
                                    <span className="text-gray-500">
                                      ({personalLimit.toLocaleString()}건)
                                    </span>
                                  </span>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              {user.teamName ? (
                                <div>
                                  <div>{user.teamBillingCycleDay ? `${user.teamBillingCycleDay}일` : '-'}</div>
                                  <div className="text-xs text-gray-500">
                                    개인: {user.billingCycleDay ? `${user.billingCycleDay}일` : '-'}
                                  </div>
                                </div>
                              ) : (
                                <span>{user.billingCycleDay ? `${user.billingCycleDay}일` : '-'}</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {new Date(user.createdAt).toLocaleDateString('ko-KR')}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {user.approvalStatus === 'PENDING' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setSelectedUser(user);
                                        setApprovalAction('APPROVED');
                                        setShowApprovalModal(true);
                                      }}
                                      className="text-green-600 hover:text-green-700"
                                    >
                                      <CheckCircle2 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setSelectedUser(user);
                                        setApprovalAction('REJECTED');
                                        setShowApprovalModal(true);
                                      }}
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                {user.isLocked && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleUnlock(user.userId, user.name);
                                    }}
                                    className="text-blue-600 hover:text-blue-700"
                                  >
                                    <Unlock className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                      <Button
                        onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                        disabled={currentPage === 0}
                        variant="outline"
                      >
                        이전
                      </Button>
                      <span className="px-4 py-2 text-sm">
                        {currentPage + 1} / {totalPages}
                      </span>
                      <Button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={currentPage >= totalPages - 1}
                        variant="outline"
                      >
                        다음
                      </Button>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {showDetailModal && detailUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>회원 상세</CardTitle>
              <CardDescription>
                {detailUser.name} ({detailUser.userId})
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {detailLoading && (
                <p className="text-sm text-gray-600">로딩 중...</p>
              )}
              {detailError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{detailError}</AlertDescription>
                </Alert>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">아이디</label>
                  <Input value={detailUser.userId} disabled className="bg-gray-100" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">로그인 유형</label>
                  <Input 
                    value={detailLoginType}
                    disabled 
                    className="bg-gray-100" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">이름</label>
                  <Input
                    value={editForm.name || ''}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    disabled={detailIsSocialLoginUser || !!detailUser.isWithdrawn}
                    className={detailIsSocialLoginUser || detailUser.isWithdrawn ? 'bg-gray-100' : undefined}
                  />
                  {detailIsSocialLoginUser && detailSocialProviderLabel && (
                    <p className="text-xs text-gray-500 mt-1">
                      {detailSocialProviderLabel} 간편로그인 사용자는 이름을 변경할 수 없습니다.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">이메일</label>
                  <Input
                    type="email"
                    value={editForm.email || ''}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    disabled={detailIsSocialLoginUser || !!detailUser.isWithdrawn}
                    className={detailIsSocialLoginUser || detailUser.isWithdrawn ? 'bg-gray-100' : undefined}
                  />
                  {detailIsSocialLoginUser && detailSocialProviderLabel && (
                    <p className="text-xs text-gray-500 mt-1">
                      {detailSocialProviderLabel} 간편로그인 사용자는 이메일을 변경할 수 없습니다.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">전화번호</label>
                  <Input
                    value={editForm.phone || ''}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    disabled={!!detailUser.isWithdrawn}
                    className={detailUser.isWithdrawn ? 'bg-gray-100' : undefined}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">회사명</label>
                  <Input
                    value={editForm.companyName || ''}
                    onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                    disabled={!!detailUser.isWithdrawn}
                    className={detailUser.isWithdrawn ? 'bg-gray-100' : undefined}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">사업자번호</label>
                  <Input
                    value={editForm.businessNumber || ''}
                    onChange={(e) => setEditForm({ ...editForm, businessNumber: e.target.value })}
                    disabled={!!detailUser.isWithdrawn}
                    className={detailUser.isWithdrawn ? 'bg-gray-100' : undefined}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">권한</label>
                  <select
                    value={editForm.userRole}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        userRole: e.target.value as UserUpdateRequest['userRole'],
                      })
                    }
                    disabled={!!detailUser.isWithdrawn}
                    className={`flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm${detailUser.isWithdrawn ? ' opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                    <option value="CLIENT">CLIENT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">승인 상태</label>
                  <select
                    value={editForm.approvalStatus}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        approvalStatus: e.target.value as UserUpdateRequest['approvalStatus'],
                      })
                    }
                    disabled={!!detailUser.isWithdrawn}
                    className={`flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm${detailUser.isWithdrawn ? ' opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="PENDING">PENDING</option>
                    <option value="APPROVED">APPROVED</option>
                    <option value="REJECTED">REJECTED</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">플랜</label>
                  <select
                    value={editForm.subscriptionPlan}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        subscriptionPlan: e.target.value as UserUpdateRequest['subscriptionPlan'],
                      })
                    }
                    disabled={!!detailUser.isWithdrawn}
                    className={`flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm${detailUser.isWithdrawn ? ' opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="FREE">FREE</option>
                    <option value="STARTER">STARTER</option>
                    <option value="PROFESSIONAL">PROFESSIONAL</option>
                    <option value="ENTERPRISE">ENTERPRISE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">활성화</label>
                  <select
                    value={editForm.enabled ? 'true' : 'false'}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        enabled: e.target.value === 'true',
                      })
                    }
                    disabled={!!detailUser.isWithdrawn}
                    className={`flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm${detailUser.isWithdrawn ? ' opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="true">활성</option>
                    <option value="false">비활성</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">가입일</label>
                  <Input
                    value={new Date(detailUser.createdAt).toLocaleString('ko-KR')}
                    disabled
                    className="bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">마지막 로그인</label>
                  <Input
                    value={detailUser.lastLoginAt ? new Date(detailUser.lastLoginAt).toLocaleString('ko-KR') : '-'}
                    disabled
                    className="bg-gray-100"
                  />
                </div>
                {detailUser.isWithdrawn && (
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-500">탈퇴일시</label>
                    <Input
                      value={detailUser.withdrawnAt ? new Date(detailUser.withdrawnAt).toLocaleString('ko-KR') : '-'}
                      disabled
                      className="bg-gray-100 text-gray-400"
                    />
                  </div>
                )}
              </div>

              {!detailUser.isWithdrawn && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium">승인 상태 변경</label>
                  <textarea
                    value={detailApprovalReason}
                    onChange={(e) => setDetailApprovalReason(e.target.value)}
                    className="w-full min-h-[80px] p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="거부 사유 또는 승인 메시지를 입력하세요"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleDetailApproval('APPROVED')}
                      className="flex-1"
                      disabled={isDetailApproving}
                    >
                      {isDetailApproving ? '승인 중...' : '승인'}
                    </Button>
                    <Button
                      onClick={() => handleDetailApproval('REJECTED')}
                      variant="destructive"
                      className="flex-1"
                      disabled={isDetailApproving}
                    >
                      {isDetailApproving ? '처리 중...' : '거부'}
                    </Button>
                    {detailUser.isLocked && (
                      <Button
                        onClick={() => handleUnlock(detailUser.userId, detailUser.name)}
                        variant="outline"
                        className="flex-1"
                        disabled={isDetailApproving}
                      >
                        잠금 해제
                      </Button>
                    )}
                  </div>

                  {/* 비밀번호 초기화 버튼 */}
                  <div className="border-t pt-4">
                    {detailIsSocialLoginUser && detailSocialProviderLabel ? (
                      <p className="text-sm text-gray-600 text-center">
                        {detailSocialProviderLabel} 간편로그인 사용자는 비밀번호를 초기화할 수 없습니다.
                      </p>
                    ) : (
                      <>
                        <Button
                          onClick={() => handleResetPassword(detailUser.userId, detailUser.name, detailUser.email)}
                          variant="outline"
                          className="w-full text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                        >
                          <KeyRound className="w-4 h-4 mr-2" />
                          비밀번호 초기화 (이메일 전송)
                        </Button>
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          랜덤 임시 비밀번호가 생성되어 {detailUser.email}로 전송됩니다
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* 증빙 발행 설정 (유료 회원만) */}
              {detailUser.subscriptionPlan !== 'FREE' && (
                <div className="border-t pt-4 space-y-2">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                    <FileText className="w-4 h-4" />
                    증빙 발행 설정
                  </h4>
                  {detailReceiptLoading ? (
                    <p className="text-xs text-gray-400">로딩 중...</p>
                  ) : detailReceiptInfo && detailReceiptInfo.receiptType !== 'NONE' ? (
                    <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">증빙 유형</span>
                        <span className="font-medium">{RECEIPT_TYPE_LABEL[detailReceiptInfo.receiptType] ?? '-'}</span>
                      </div>
                      {detailReceiptInfo.receiptType === 'CASH_RECEIPT' && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-500">용도</span>
                            <span className="font-medium">{CASH_RECEIPT_PURPOSE_LABEL[detailReceiptInfo.cashReceiptPurpose ?? ''] ?? '-'}</span>
                          </div>
                          {(detailReceiptInfo.cashReceiptPurpose === 'PERSONAL_DEDUCTION' ? detailReceiptInfo.cashReceiptNumber : detailReceiptInfo.cashReceiptNumber) && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">
                                {detailReceiptInfo.cashReceiptPurpose === 'PERSONAL_DEDUCTION' ? '휴대폰번호' : '사업자번호'}
                              </span>
                              <span className="font-medium font-mono">
                                {detailReceiptInfo.cashReceiptNumber}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                      {detailReceiptInfo.receiptType === 'TAX_INVOICE' && (
                        <>
                          {detailReceiptInfo.bizRegNo && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">사업자번호</span>
                              <span className="font-medium font-mono">{detailReceiptInfo.bizRegNo}</span>
                            </div>
                          )}
                          {detailReceiptInfo.bizName && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">상호명</span>
                              <span className="font-medium">{detailReceiptInfo.bizName}</span>
                            </div>
                          )}
                          {detailReceiptInfo.bizCeoName && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">대표자명</span>
                              <span className="font-medium">{detailReceiptInfo.bizCeoName}</span>
                            </div>
                          )}
                          {detailReceiptInfo.taxInvoiceEmail && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">발행 이메일</span>
                              <span className="font-medium">{detailReceiptInfo.taxInvoiceEmail}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : detailReceiptInfo?.receiptType === 'NONE' ? (
                    <p className="text-xs text-gray-400">발행안함으로 설정되어 있습니다.</p>
                  ) : (
                    <p className="text-xs text-gray-400">증빙 설정 정보가 없습니다.</p>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={handleUpdateUser} className="flex-1" disabled={!!detailUser.isWithdrawn}>
                  회원 정보 수정
                </Button>
                <Button
                  onClick={() => {
                    setShowDetailModal(false);
                    setDetailUser(null);
                    setDetailReceiptInfo(null);
                    setDetailError(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  닫기
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {approvalAction === 'APPROVED' ? '회원 승인' : '회원 거부'}
              </CardTitle>
              <CardDescription>
                {selectedUser.name}님({selectedUser.userId})을{' '}
                {approvalAction === 'APPROVED' ? '승인' : '거부'}하시겠습니까?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {approvalAction === 'REJECTED' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    거부 사유 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={approvalReason}
                    onChange={(e) => setApprovalReason(e.target.value)}
                    className="w-full min-h-[100px] p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="거부 사유를 입력해주세요"
                    required
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={handleApprovalChange} className="flex-1" disabled={isApproving}>
                  {isApproving
                    ? approvalAction === 'APPROVED'
                      ? '승인 중...'
                      : '처리 중...'
                    : '확인'}
                </Button>
                <Button
                  onClick={() => {
                    if (isApproving) return;
                    setShowApprovalModal(false);
                    setSelectedUser(null);
                    setApprovalReason('');
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={isApproving}
                >
                  취소
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </PageContainer>
  );
};
